import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import PageHeader from "@/components/site/PageHeader";
import { useSEO } from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Check,
    ArrowRight,
    Stamp,
    Globe2,
    Loader2,
    CheckCircle2,
    XCircle,
    ShieldCheck,
    Sparkles,
    Info,
    ExternalLink,
} from "lucide-react";
import { LANSSTYRELSEN_NP_URL } from "@/components/site/NotariusBanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inputClass =
    "bg-[#0A0A0B] border-white/10 rounded-sm text-[#F4F4F5] placeholder:text-[#A1A1AA]/60 h-11";

// Prislista i tre grupper — täcker även tjänster som kräver offert.
const STANDARD_TJANSTER = [
    { name: "Bevittning av namnteckning", price: "500 kr" },
    { name: "Vidimering av kopia", price: "500 kr" },
    { name: "Apostille", price: "650 kr per dokument" },
    { name: "Intyg om namnunderskrift", price: "500 kr" },
    { name: "Fullmakter/intyg — enklare handlingar", price: "från 750 kr" },
    { name: "Firmateckningsintyg", price: "850 kr" },
    { name: "Intyg av pass/id-handling", price: "500 kr" },
    { name: "Certifiering av översättning", price: "från 950 kr" },
    { name: "Affidavit / deklaration under ed", price: "från 1 500 kr" },
    { name: "Närvaro-/levnadsintyg", price: "850 kr" },
    { name: "Bestyrkande av examenshandlingar", price: "500 kr" },
];

const FORETAG_TJANSTER = [
    { name: "Bolagshandlingar (styrelseprotokoll m.m.)", price: "från 1 250 kr" },
    { name: "Due diligence / intygspaket", price: "enligt offert" },
    { name: "Certifiering av större dokumentpaket", price: "enligt offert" },
    { name: "Utländska handlingar / komplexa ärenden", price: "från 1 500 kr" },
];

const UTRYCKNING_TJANSTER = [
    { name: "Förrättning utanför kontor dagtid", price: "3 000 kr/timme" },
    { name: "Förrättning kväll/helg", price: "4 000 kr/timme" },
    { name: "Resekostnad", price: "65 kr/mil" },
    { name: "Akutärende samma dag", price: "tillägg 750–1 500 kr" },
];

export default function NotariusPublicus() {
    useSEO({
        title: "Notarius Publicus — Bevittning, Apostille & Intyg",
        description:
            "Notarius publicus-tjänster till fasta priser: bevittning av namnteckning, apostille, vidimering, firmateckningsintyg, levnadsintyg m.m. Beställ online med 50 kr rabatt.",
    });

    const [products, setProducts] = useState([]);
    const [discount, setDiscount] = useState(50);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
    const [submitting, setSubmitting] = useState(false);
    const [statusOverlay, setStatusOverlay] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API}/notarius/products`);
                setProducts(data.items || []);
                if (typeof data.online_discount_sek === "number") {
                    setDiscount(data.online_discount_sek);
                }
            } catch {
                toast.error("Kunde inte hämta tjänsterna.");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    // Handle return from Stripe
    useEffect(() => {
        const payment = searchParams.get("payment");
        const sid = searchParams.get("session_id");
        if (payment === "success" && sid) {
            setStatusOverlay({ kind: "success", loading: true });
            (async () => {
                try {
                    const { data } = await axios.get(
                        `${API}/shop/order-status/${sid}`,
                    );
                    setStatusOverlay({ kind: "success", order: data });
                } catch {
                    setStatusOverlay({ kind: "success", order: null });
                }
            })();
        } else if (payment === "cancelled") {
            setStatusOverlay({ kind: "cancelled" });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const clearOverlay = () => {
        setStatusOverlay(null);
        searchParams.delete("payment");
        searchParams.delete("session_id");
        setSearchParams(searchParams, { replace: true });
    };

    const openCheckout = (p) => {
        setSelected(p);
        setForm({ name: "", email: "", phone: "", notes: "" });
    };

    const submit = async () => {
        if (!form.name.trim() || !form.email.trim()) {
            toast.error("Namn och e-post krävs.");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API}/shop/checkout`, {
                product_id: selected.id,
                origin: window.location.origin,
                customer_name: form.name,
                customer_email: form.email,
                customer_phone: form.phone,
                notes: form.notes,
            });
            if (data?.url) {
                window.location.href = data.url;
            } else {
                throw new Error("ingen checkout-url");
            }
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Kunde inte starta betalningen.",
            );
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="page-notarius">
            <PageHeader
                kicker="Notarius Publicus"
                title="Officiella intyg —"
                accent="när det verkligen räknas."
                description="Bevittning av namnteckning, apostille, vidimering, firmateckningsintyg och andra notariella handlingar — giltiga både i Sverige och internationellt. Beställ online med 50 kr rabatt, eller boka tid på något av våra kontor."
            />

            {/* Online order — fasta priser med rabatt */}
            <section className="py-20 bg-[#0A0A0B]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-end justify-between gap-6 mb-10 flex-wrap">
                        <div>
                            <p className="overline">Beställ online</p>
                            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl text-[#F4F4F5] tracking-tight">
                                Välj tjänst och{" "}
                                <span className="italic text-[#C4A977]">spara {discount} kr.</span>
                            </h2>
                            <p className="mt-4 text-sm text-[#A1A1AA] max-w-2xl leading-relaxed">
                                Betala säkert med kort, Klarna eller Swish. Efter betalning får du ett
                                bokningsmejl med tid och vilka handlingar du ska ha med dig till besöket.
                            </p>
                        </div>
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#C4A977]/10 border border-[#C4A977]/40 rounded-sm">
                            <Sparkles size={12} className="text-[#C4A977]" />
                            <span className="text-[10px] tracking-[0.2em] uppercase text-[#C4A977] font-semibold">
                                {discount} kr rabatt vid online-beställning
                            </span>
                        </div>
                    </div>

                    {loading ? (
                        <p className="text-center text-[#A1A1AA]">Läser in...</p>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                            {products.map((p, i) => (
                                <motion.div
                                    key={p.id}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-60px" }}
                                    transition={{ duration: 0.6, delay: i * 0.05 }}
                                    data-testid={`notarius-card-${p.id}`}
                                    className="flex flex-col bg-[#111113] border border-white/5 hover:border-[#C4A977]/40 p-8 rounded-sm transition-colors duration-500"
                                >
                                    <div className="flex items-center gap-2">
                                        <Stamp size={14} className="text-[#C4A977]" />
                                        <p className="overline !text-[0.65rem]">{p.group}</p>
                                    </div>
                                    <h3 className="mt-3 font-serif text-2xl text-[#F4F4F5] leading-tight">
                                        {p.name}
                                    </h3>
                                    <p className="mt-3 text-sm text-[#A1A1AA] leading-relaxed">
                                        {p.tagline}
                                    </p>
                                    <div className="my-6 flex items-end gap-3">
                                        <div className="flex items-baseline gap-2">
                                            <span className="font-serif text-4xl text-[#C4A977] leading-none">
                                                {(p.price_sek - discount).toLocaleString("sv-SE")}
                                            </span>
                                            <span className="text-[#A1A1AA] text-xs pb-1">kr</span>
                                        </div>
                                        <span className="text-xs text-[#A1A1AA] line-through pb-1">
                                            {p.price_sek.toLocaleString("sv-SE")} kr
                                        </span>
                                    </div>
                                    <ul className="space-y-2 text-sm text-[#F4F4F5]/90 flex-1">
                                        {p.bullets.map((b) => (
                                            <li key={b} className="flex items-start gap-2">
                                                <Check size={14} className="text-[#C4A977] mt-1 shrink-0" />
                                                <span>{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <p className="mt-6 text-xs text-[#A1A1AA]">{p.delivery}</p>
                                    <Button
                                        onClick={() => openCheckout(p)}
                                        data-testid={`notarius-buy-${p.id}`}
                                        className="mt-6 w-full bg-[#C4A977] hover:bg-[#DBC08F] text-black rounded-sm h-11 tracking-wide group"
                                    >
                                        Beställ online
                                        <ArrowRight
                                            size={14}
                                            className="ml-2 transition-transform duration-300 group-hover:translate-x-1"
                                        />
                                    </Button>
                                </motion.div>
                            ))}
                        </div>
                    )}

                    {/* Trust row */}
                    <div className="mt-16 bg-[#111113] border border-white/5 p-8 rounded-sm grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="flex gap-3">
                            <ShieldCheck size={18} className="text-[#C4A977] mt-1 shrink-0" />
                            <div>
                                <p className="overline !text-[0.65rem]">Officiell myndighetsutövning</p>
                                <p className="mt-1 text-sm text-[#F4F4F5]/90">
                                    Daniel Madsen är förordnad av Länsstyrelsen för Vårgårda, Herrljunga, Vara och Alingsås kommun — intyg giltiga både nationellt och internationellt.
                                </p>
                                <a
                                    href={LANSSTYRELSEN_NP_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="notarius-page-lansstyrelsen-link"
                                    className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#C4A977] hover:text-[#DBC08F] transition-colors"
                                >
                                    Läs mer hos Länsstyrelsen
                                    <ExternalLink size={12} />
                                </a>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <Globe2 size={18} className="text-[#C4A977] mt-1 shrink-0" />
                            <div>
                                <p className="overline !text-[0.65rem]">Apostille & internationellt</p>
                                <p className="mt-1 text-sm text-[#F4F4F5]/90">
                                    Handlingar förberedda enligt Haagkonventionen — giltiga i över 125 länder.
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <Check size={18} className="text-[#C4A977] mt-1 shrink-0" />
                            <div>
                                <p className="overline !text-[0.65rem]">Smidig process</p>
                                <p className="mt-1 text-sm text-[#F4F4F5]/90">
                                    Betala online, kom förbi kontoret — ofta klart samma dag.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Full prislista */}
            <section className="py-20 bg-[#111113] border-t border-white/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="mb-14">
                        <p className="overline">Prislista</p>
                        <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl text-[#F4F4F5] tracking-tight">
                            Fullständig{" "}
                            <span className="italic text-[#C4A977]">prisöversikt.</span>
                        </h2>
                        <p className="mt-4 text-sm text-[#A1A1AA] max-w-2xl leading-relaxed">
                            Alla priser är inklusive moms. Tjänster märkta <em>från X kr</em> eller <em>enligt offert</em> beställs via kontaktformulär eller telefon för en individuell prisbild.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <PriceTable title="Standardtjänster" items={STANDARD_TJANSTER} />
                        <PriceTable title="Företag & större ärenden" items={FORETAG_TJANSTER} />
                        <PriceTable title="Utryckning & särskilda tjänster" items={UTRYCKNING_TJANSTER} />
                    </div>

                    {/* Villkor */}
                    <div className="mt-14 bg-[#0A0A0B] border border-white/5 p-8 rounded-sm">
                        <div className="flex items-center gap-2 mb-4">
                            <Info size={14} className="text-[#C4A977]" />
                            <p className="overline !text-[0.65rem]">Villkor</p>
                        </div>
                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3 text-sm text-[#F4F4F5]/90">
                            <li className="flex items-start gap-2">
                                <Check size={14} className="text-[#C4A977] mt-1 shrink-0" />
                                <span>Minsta debitering är 500 kr.</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <Check size={14} className="text-[#C4A977] mt-1 shrink-0" />
                                <span>Påbörjad kvart debiteras vid tidsbaserade uppdrag.</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <Check size={14} className="text-[#C4A977] mt-1 shrink-0" />
                                <span>Betalning sker via Swish, kort eller Klarna — online eller på plats.</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <Check size={14} className="text-[#C4A977] mt-1 shrink-0" />
                                <span>Handlingar på andra språk än svenska eller engelska kan medföra tillägg.</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <Check size={14} className="text-[#C4A977] mt-1 shrink-0" />
                                <span>För större volymer lämnas pris enligt offert — välkommen att höra av dig.</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <Sparkles size={14} className="text-[#C4A977] mt-1 shrink-0" />
                                <span>Online-beställning ger 50 kr rabatt på fastprisade tjänster.</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Checkout dialog */}
            <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
                <DialogContent className="bg-[#111113] border-white/10 text-[#F4F4F5] max-w-lg">
                    {selected && (
                        <>
                            <DialogHeader>
                                <DialogDescription className="overline !text-[0.65rem]">
                                    {selected.group}  ·  online {(selected.price_sek - discount).toLocaleString("sv-SE")} kr
                                    <span className="text-[#A1A1AA]/70 line-through ml-2">
                                        {selected.price_sek.toLocaleString("sv-SE")} kr
                                    </span>
                                </DialogDescription>
                                <DialogTitle className="font-serif text-2xl">
                                    Beställ {selected.name}
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 mt-2">
                                <div>
                                    <Label className="text-xs text-[#A1A1AA] uppercase tracking-widest">Ditt namn *</Label>
                                    <Input
                                        data-testid="notarius-input-name"
                                        value={form.name}
                                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                        placeholder="För- och efternamn"
                                        className={`mt-2 ${inputClass}`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs text-[#A1A1AA] uppercase tracking-widest">E-post *</Label>
                                    <Input
                                        data-testid="notarius-input-email"
                                        type="email"
                                        value={form.email}
                                        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                                        placeholder="namn@exempel.se"
                                        className={`mt-2 ${inputClass}`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs text-[#A1A1AA] uppercase tracking-widest">Telefon</Label>
                                    <Input
                                        data-testid="notarius-input-phone"
                                        value={form.phone}
                                        onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                                        placeholder="+46 70 000 00 00"
                                        className={`mt-2 ${inputClass}`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs text-[#A1A1AA] uppercase tracking-widest">Meddelande / önskemål</Label>
                                    <Textarea
                                        data-testid="notarius-input-notes"
                                        value={form.notes}
                                        onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                                        rows={3}
                                        placeholder="T.ex. vilket dokument, önskat kontor, tid som passar, mottagande land."
                                        className={`mt-2 ${inputClass} !h-auto py-3`}
                                    />
                                </div>
                                <p className="text-xs text-[#A1A1AA]">
                                    Efter betalning får du ett mejl med bokningsbekräftelse. Handling eller id-handling medtas vid besök — vi kontaktar dig samma arbetsdag om något behöver kompletteras.
                                </p>
                            </div>
                            <DialogFooter className="mt-4">
                                <Button
                                    onClick={submit}
                                    disabled={submitting}
                                    data-testid="notarius-submit"
                                    className="bg-[#C4A977] hover:bg-[#DBC08F] text-black rounded-sm h-11 px-8"
                                >
                                    {submitting ? (
                                        <><Loader2 size={14} className="mr-2 animate-spin" /> Startar betalning...</>
                                    ) : (
                                        <>Betala {(selected.price_sek - discount).toLocaleString("sv-SE")} kr</>
                                    )}
                                </Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Payment status overlay */}
            <Dialog open={!!statusOverlay} onOpenChange={(v) => !v && clearOverlay()}>
                <DialogContent
                    data-testid="notarius-status-overlay"
                    className="bg-[#111113] border-white/10 text-[#F4F4F5] max-w-md text-center"
                >
                    {statusOverlay?.kind === "success" ? (
                        <div className="py-4">
                            <CheckCircle2 size={42} className="text-emerald-400 mx-auto" />
                            <h3 className="mt-5 font-serif text-3xl">Tack för din beställning!</h3>
                            <p className="mt-3 text-sm text-[#A1A1AA] leading-relaxed">
                                Vi har mottagit din betalning. En bokningsbekräftelse är på väg till
                                din inkorg — vi hör av oss samma arbetsdag med tid och plats.
                            </p>
                            {statusOverlay.order?.id && (
                                <p className="mt-4 text-xs text-[#A1A1AA]">
                                    Ordernummer: <span className="text-[#C4A977]">{statusOverlay.order.id}</span>
                                </p>
                            )}
                            <Button
                                onClick={clearOverlay}
                                className="mt-8 bg-[#C4A977] hover:bg-[#DBC08F] text-black rounded-sm"
                            >
                                Stäng
                            </Button>
                        </div>
                    ) : (
                        <div className="py-4">
                            <XCircle size={42} className="text-amber-300 mx-auto" />
                            <h3 className="mt-5 font-serif text-3xl">Betalningen avbröts</h3>
                            <p className="mt-3 text-sm text-[#A1A1AA] leading-relaxed">
                                Ingen debitering har gjorts. Du kan försöka igen när som helst — eller kontakta oss om du har frågor.
                            </p>
                            <Button
                                onClick={clearOverlay}
                                className="mt-8 bg-[#C4A977] hover:bg-[#DBC08F] text-black rounded-sm"
                            >
                                Stäng
                            </Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function PriceTable({ title, items }) {
    return (
        <div
            data-testid={`notarius-pricetable-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="bg-[#111113] border border-white/5 rounded-sm overflow-hidden"
        >
            <div className="px-6 pt-6 pb-4 border-b border-white/5">
                <p className="overline !text-[0.65rem]">{title}</p>
            </div>
            <ul>
                {items.map((it, idx) => (
                    <li
                        key={it.name}
                        className={`px-6 py-4 flex items-start justify-between gap-4 text-sm ${
                            idx !== items.length - 1 ? "border-b border-white/5" : ""
                        }`}
                    >
                        <span className="text-[#F4F4F5]/90 leading-snug">{it.name}</span>
                        <span className="text-[#C4A977] whitespace-nowrap font-medium">{it.price}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}
